import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Loader2, Copy, Download, AlertTriangle, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-Factor Authentication settings card.
 * Drop-in for the Settings page — handles full lifecycle: setup → enable → disable → regenerate codes.
 */
export default function TwoFactorCard({ token, userEmail }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // enrollment state
  const [enrollment, setEnrollment] = useState(null);  // { secret, otpauth_url, qr_code_png_base64 }
  const [enableCode, setEnableCode] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // disable state
  const [disablePw, setDisablePw] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);

  // regenerate state
  const [regenShowing, setRegenShowing] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/auth/2fa/status`, { headers });
      setStatus(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadStatus(); }, []); // eslint-disable-line

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers });
      setEnrollment(res.data);
      setEnableCode('');
      setRecoveryCodes([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start 2FA setup');
    } finally { setLoading(false); }
  };

  const enable = async () => {
    if (!/^\d{6}$/.test(enableCode)) { toast.error('Enter the 6-digit code shown in your app'); return; }
    setEnabling(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/enable`, { code: enableCode }, { headers });
      setRecoveryCodes(res.data.recovery_codes || []);
      setEnrollment(null);
      setEnableCode('');
      await loadStatus();
      toast.success('2FA is now active — save your recovery codes');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setEnabling(false); }
  };

  const disable = async () => {
    if (!disablePw || !/^\d{6}$/.test(disableCode)) { toast.error('Fill in password + 6-digit code'); return; }
    setDisabling(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password: disablePw, code: disableCode }, { headers });
      setDisablePw(''); setDisableCode('');
      await loadStatus();
      toast.success('2FA disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not disable');
    } finally { setDisabling(false); }
  };

  const regenerate = async () => {
    if (!disablePw || !/^\d{6}$/.test(disableCode)) { toast.error('Fill in password + 6-digit code'); return; }
    setDisabling(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/regenerate-recovery-codes`, { password: disablePw, code: disableCode }, { headers });
      setRecoveryCodes(res.data.recovery_codes || []);
      setDisablePw(''); setDisableCode('');
      setRegenShowing(false);
      await loadStatus();
      toast.success('Fresh recovery codes issued — previous codes are now invalid');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not regenerate');
    } finally { setDisabling(false); }
  };

  const downloadCodes = () => {
    const text = `KStreak Recovery Codes for ${userEmail}\nIssued: ${new Date().toISOString()}\n\nEach code works once. Store safely.\n\n${recoveryCodes.join('\n')}\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `kstreak-recovery-codes-${Date.now()}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery codes copied');
  };

  if (!status) return null;

  return (
    <Card className="border-[#E5E7EB] mb-6" data-testid="twofa-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {status.enabled ? <ShieldCheck className="w-5 h-5 text-[#34C759]" /> : <ShieldOff className="w-5 h-5 text-[#9CA3AF]" />}
            Two-Factor Authentication
          </CardTitle>
          <Badge
            className={status.enabled ? "text-[10px] bg-[#34C759]/15 text-[#34C759] border-0" : "text-[10px] text-[#9CA3AF] border-[#9CA3AF]/40"}
            variant={status.enabled ? undefined : "outline"}
            data-testid="twofa-status-badge"
          >
            {status.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Recovery codes just issued — highest-priority view */}
        {recoveryCodes.length > 0 ? (
          <div className="mb-4 p-4 border border-[#FF9500] bg-[#FF9500]/5 rounded" data-testid="recovery-codes-panel">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#FF9500] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#0A0A0A]">Save these recovery codes now</p>
                <p className="text-xs text-[#4B5563] mt-1">
                  Each code works once and lets you log in without your authenticator app. You won't see them again —
                  download or copy to a password manager immediately.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs mb-3" data-testid="recovery-codes-list">
              {recoveryCodes.map((code, i) => (
                <code key={i} className="bg-white border border-[#E5E7EB] rounded px-2 py-1.5 text-center">
                  {code}
                </code>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyCodes} data-testid="copy-codes-btn">
                <Copy className="w-3 h-3 mr-1" />Copy
              </Button>
              <Button size="sm" variant="outline" onClick={downloadCodes} data-testid="download-codes-btn">
                <Download className="w-3 h-3 mr-1" />Download .txt
              </Button>
              <Button size="sm" onClick={() => setRecoveryCodes([])} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="codes-saved-btn">
                I've saved them
              </Button>
            </div>
          </div>
        ) : null}

        {/* Enrollment (setup) */}
        {enrollment && !status.enabled ? (
          <div data-testid="twofa-enrollment">
            <p className="text-sm text-[#4B5563] mb-3">
              Scan the QR code below with Google Authenticator, Authy, 1Password, or any TOTP app.
              Then enter the 6-digit code it generates to confirm.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <img
                src={`data:image/png;base64,${enrollment.qr_code_png_base64}`}
                alt="2FA QR code"
                className="w-48 h-48 border border-[#E5E7EB] rounded bg-white"
                data-testid="twofa-qr-img"
              />
              <div className="flex-1">
                <Label className="text-xs">Can't scan? Enter this secret manually</Label>
                <code className="block mt-1 p-2 bg-[#F3F4F6] rounded text-xs font-mono break-all" data-testid="twofa-manual-secret">
                  {enrollment.secret}
                </code>
                <Label className="text-xs mt-3 block">Enter the 6-digit code from your app</Label>
                <Input
                  value={enableCode}
                  onChange={e => setEnableCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="mt-1 font-mono text-lg tracking-wider"
                  maxLength={6}
                  data-testid="twofa-enable-code-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={enable} disabled={enabling || enableCode.length !== 6} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="twofa-enable-btn">
                {enabling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Activate 2FA
              </Button>
              <Button variant="outline" onClick={() => setEnrollment(null)}>Cancel</Button>
            </div>
          </div>
        ) : null}

        {/* Regenerate-recovery OR disable section */}
        {status.enabled && !enrollment ? (
          <div>
            <p className="text-sm text-[#4B5563] mb-3">
              2FA is protecting your account. You have <strong>{status.recovery_codes_remaining}</strong> recovery code
              {status.recovery_codes_remaining === 1 ? '' : 's'} remaining.
              {status.recovery_codes_remaining <= 2 && (
                <span className="text-[#FF9500]"> (Low — regenerate soon)</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRegenShowing(v => !v)}
                data-testid="twofa-regenerate-toggle"
              >
                <KeyRound className="w-3 h-3 mr-1" />
                {regenShowing ? 'Hide' : 'Regenerate recovery codes'}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs">Current password</Label>
                <Input type="password" value={disablePw} onChange={e => setDisablePw(e.target.value)} className="mt-1" data-testid="twofa-disable-password-input" />
              </div>
              <div>
                <Label className="text-xs">6-digit code from your app</Label>
                <Input
                  value={disableCode}
                  onChange={e => setDisableCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                  className="mt-1 font-mono tracking-wider"
                  maxLength={6}
                  data-testid="twofa-disable-code-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {regenShowing ? (
                <Button onClick={regenerate} disabled={disabling} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="twofa-regenerate-btn">
                  {disabling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Issue new recovery codes
                </Button>
              ) : (
                <Button onClick={disable} disabled={disabling} variant="destructive" data-testid="twofa-disable-btn">
                  {disabling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
                  Turn off 2FA
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {/* Off + not mid-enrollment → Call-to-action */}
        {!status.enabled && !enrollment ? (
          <div>
            <p className="text-sm text-[#4B5563] mb-3">
              Add a second check at login using an authenticator app (Google Authenticator, Authy, 1Password).
              Even if your password is stolen, accounts with 2FA are effectively un-hijackable.
            </p>
            <Button onClick={startSetup} disabled={loading} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="twofa-setup-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Enable 2FA
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
